/**
 * Page-side helper to message the upload Service Worker.
 *
 * Methods return a promise that resolves once the SW acknowledges receipt
 * (NOT once the upload finishes). Progress is reported via BroadcastChannel
 * which the CustomerFilesStore subscribes to separately (Task 8).
 */

async function activeSW() {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service Worker not supported in this browser");
  }
  const reg = await navigator.serviceWorker.ready;
  if (!reg.active) throw new Error("no active SW");
  return reg.active;
}

// The page's request.js injects an Authorization header on every /api call
// (src/queries/request.js:160) from the in-memory Cognito session. The SW
// does raw fetch() and needs the same header passed in via postMessage. We
// take the token from a getter that the app registers at boot — reading the
// `plex_ui_key` cookie doesn't work because UserStore scopes it to path=/api,
// so document.cookie can't see it from a /v2/* page.
let authGetter = () => null;
export function setAuthGetter(fn) { authGetter = fn; }
function authHeader() {
  try { return authGetter(); } catch (_) { return null; }
}

// PLEX-3945: no Bearer token means the user is signed out or the session expired.
// Fail fast with a clear, friendlyError-mappable error (status 401) instead of firing
// a doomed init-upload that 401s server-side (Sentry WEBAPP-3Z) and wastes a round-trip.
function unauthenticatedError() {
  return Object.assign(
    new Error("Your session has expired. Please refresh the page and sign in again."),
    { status: 401 },
  );
}

export async function startUpload(programId, projectId, file) {
  const authorization = authHeader();
  if (!authorization) throw unauthenticatedError();
  const sw = await activeSW();
  sw.postMessage({
    type: "start_upload",
    program_id: programId,
    project_id: projectId,
    file,
    name: file.name,
    content_type: file.type || "application/octet-stream",
    size_bytes: file.size,
    authorization,
  });
}

export async function abortUpload(fileId) {
  const sw = await activeSW();
  sw.postMessage({ type: "abort", file_id: fileId, authorization: authHeader() });
}

export async function listInflight() {
  const sw = await activeSW();
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = (e) => resolve(e.data);
    sw.postMessage({ type: "list_uploads" }, [channel.port2]);
  });
}

export async function resumeUpload(fileId, file) {
  const authorization = authHeader();
  if (!authorization) throw unauthenticatedError();
  const sw = await activeSW();
  sw.postMessage({ type: "resume_upload", file_id: fileId, file, authorization });
}

/**
 * Subscribe to the SW's progress BroadcastChannel.
 *
 * handler is called with each {type, file_id, progress?, reason?} message.
 * Returns an unsubscribe function. No-op (returns a no-op unsubscribe) if
 * BroadcastChannel is unavailable.
 */
export function subscribeToProgress(handler) {
  if (typeof BroadcastChannel === "undefined") return () => {};
  const channel = new BroadcastChannel("plex-uploads");
  channel.onmessage = (event) => handler(event.data);
  return () => channel.close();
}
